import React from 'react';
import { useState, useMemo } from './shared';
import { I, fmt, Sparkline, SprayDiamond, ErrorsDiamond } from './shared';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = e => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

/* Mobile stat pill */
function StatPill({ label, value, hi }) {
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',minWidth:36,flex:'1 1 0'}}>
      <span style={{fontSize:8,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',color:'var(--text-3)'}}>{label}</span>
      <span style={{fontSize:13,fontWeight:hi?700:600,color:hi?'var(--text)':'var(--text-2)',fontFamily:'var(--font-mono)'}}>{value??'—'}</span>
    </div>
  );
}

/* Mobile batting card */
function BattingCard({ p, onOpenPlayer }) {
  return (
    <button onClick={()=>onOpenPlayer(p)} style={{
      display:'flex',flexDirection:'column',gap:8,padding:'10px 12px',
      background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,
      textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',width:'100%',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
        <div style={{fontWeight:700,fontSize:13,flex:1}}>{p.playerName.toUpperCase()}</div>
        <div style={{fontFamily:'var(--font-mono)',fontSize:15,fontWeight:700,color:'var(--text)'}}>{fmt.avg(p.OPS)}</div>
        <div style={{fontSize:10,color:'var(--text-3)'}}>OPS</div>
      </div>
      <div style={{display:'flex',gap:4,borderTop:'1px solid var(--border)',paddingTop:8}}>
        <StatPill label="AVG" value={fmt.avg(p.AVG)}/>
        <StatPill label="OBP" value={fmt.avg(p.OBP)}/>
        <StatPill label="SLG" value={fmt.avg(p.SLG)}/>
        <StatPill label="PA"  value={p.PA}/>
        <StatPill label="H"   value={p.H}/>
        <StatPill label="HR"  value={p.HR}/>
        <StatPill label="RBI" value={p.RBI}/>
        <StatPill label="SB"  value={p.SB}/>
      </div>
    </button>
  );
}

/* Mobile pitching card */
function PitchingCard({ p, onOpenPlayer }) {
  return (
    <button onClick={()=>onOpenPlayer({...p,playerId:'pit_'+p.playerName})} style={{
      display:'flex',flexDirection:'column',gap:8,padding:'10px 12px',
      background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,
      textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',width:'100%',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
        <div style={{fontWeight:700,fontSize:13,flex:1}}>{p.playerName.toUpperCase()}</div>
        <div style={{fontFamily:'var(--font-mono)',fontSize:15,fontWeight:700,color:'var(--good)'}}>{(p.ERA||0).toFixed(2)}</div>
        <div style={{fontSize:10,color:'var(--text-3)'}}>ERA</div>
      </div>
      <div style={{display:'flex',gap:4,borderTop:'1px solid var(--border)',paddingTop:8}}>
        <StatPill label="App"   value={p.Appearances}/>
        <StatPill label="IP"    value={fmt.ip(p.IP)}/>
        <StatPill label="WHIP"  value={(p.WHIP||0).toFixed(2)}/>
        <StatPill label="K/7"   value={(p.K_per_7||0).toFixed(1)}/>
        <StatPill label="K"     value={p.SO}/>
        <StatPill label="BB"    value={p.BB}/>
        <StatPill label="H"     value={p.H}/>
        <StatPill label="ER"    value={p.ER}/>
      </div>
    </button>
  );
}

/* Mobile game log card */
function GameCard({ g, onOpenGame }) {
  function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt)) return String(d);
    return dt.toLocaleDateString('en-US',{month:'short',day:'numeric'});
  }
  const isWin = g.Result === 'W';
  return (
    <button onClick={()=>onOpenGame(g)} style={{
      display:'flex',flexDirection:'column',gap:8,padding:'10px 12px',
      background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,
      textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',width:'100%',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span className={`result-pill ${g.Result}`}>{g.Result}</span>
        <div style={{fontWeight:700,fontSize:13,flex:1}}>{g.linkText}</div>
        <div style={{fontFamily:'var(--font-mono)',fontSize:14,fontWeight:700}}>
          <span style={isWin?{color:'var(--good)'}:{}}>{g.bat_R}</span>
          <span style={{color:'var(--text-3)',fontWeight:400,margin:'0 3px'}}>–</span>
          <span style={!isWin?{color:'var(--red)'}:{color:'var(--text-3)'}}>{g.pit_R}</span>
        </div>
        <div style={{fontSize:10,color:'var(--text-3)'}}>{fmtDate(g.date)}</div>
      </div>
      <div style={{display:'flex',gap:4,borderTop:'1px solid var(--border)',paddingTop:8}}>
        <StatPill label="AB"  value={g.bat_AB}/>
        <StatPill label="H"   value={g.bat_H}/>
        <StatPill label="RBI" value={g.bat_RBI}/>
        <StatPill label="BB"  value={g.bat_BB}/>
        <StatPill label="K"   value={g.bat_SO}/>
        <StatPill label="IP"  value={fmt.ip(g.pit_IP)}/>
        <StatPill label="ER"  value={g.pit_ER}/>
        <StatPill label="K"   value={g.pit_SO}/>
      </div>
    </button>
  );
}

function SortHeader({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const dir = active ? sort.dir : null;
  return (
    <th className={`${col.sticky ? 'first' : ''} ${active ? 'active' : ''}`} onClick={() => onSort(col.key, col.numeric !== false)}>
      {col.label}<span className="sort">{dir === 'desc' ? '▼' : dir === 'asc' ? '▲' : ''}</span>
    </th>
  );
}

function applySort(rows, sort, isNumeric) {
  const xs = rows.slice();
  xs.sort((a, b) => {
    let av = a[sort.key], bv = b[sort.key];
    if (sort.key === 'playerName' || !isNumeric) {
      av = String(av||''); bv = String(bv||'');
      return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    av = Number.isFinite(+av) ? +av : -Infinity;
    bv = Number.isFinite(+bv) ? +bv : -Infinity;
    return sort.dir === 'asc' ? av - bv : bv - av;
  });
  return xs;
}

function exportCSV(cols, rows, name) {
  const headers = cols.map(c => c.label).join(',');
  const lines = rows.map(r => cols.map(c => {
    const v = r[c.key];
    return v == null ? '' : String(v).includes(',') ? `"${v}"` : v;
  }).join(','));
  const blob = new Blob([headers + '\n' + lines.join('\n')], { type: 'text/csv' });
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `${name}.csv` });
  a.click(); URL.revokeObjectURL(a.href);
}

/* ─── BATTING ────────────────────────────────────────────── */
const BATTING_COLS = [
  { key: 'playerName', label: 'Player', sticky: true, numeric: false },
  { key: 'Games', label: 'G' }, { key: 'PA', label: 'PA' }, { key: 'AB', label: 'AB' },
  { key: 'H', label: 'H' }, { key: '2B', label: '2B' }, { key: '3B', label: '3B' },
  { key: 'HR', label: 'HR' }, { key: 'TB', label: 'TB' }, { key: 'R', label: 'R' },
  { key: 'RBI', label: 'RBI' }, { key: 'BB', label: 'BB' }, { key: 'HBP', label: 'HBP' },
  { key: 'SO', label: 'SO' }, { key: 'SB', label: 'SB' }, { key: 'CS', label: 'CS' },
  { key: 'BB_pct', label: 'BB%', fmt: 'pct1' }, { key: 'SO_pct', label: 'SO%', fmt: 'pct1' },
  { key: 'AVG', label: 'AVG', fmt: 'avg', rate: true }, { key: 'OBP', label: 'OBP', fmt: 'avg', rate: true },
  { key: 'SLG', label: 'SLG', fmt: 'avg', rate: true }, { key: 'ISO', label: 'ISO', fmt: 'avg', rate: true },
  { key: 'OPS', label: 'OPS', fmt: 'avg', rate: true },
];

export function BattingTab({ stats, onOpenPlayer }) {
  const [sort, setSort] = useState({ key: 'OPS', dir: 'desc' });
  const [q, setQ] = useState('');
  const isMobile = useIsMobile();
  const all = stats.battingSeason.filter(p => p.PA > 0);
  const filtered = q ? all.filter(p => p.playerName.toLowerCase().includes(q.toLowerCase())) : all;
  const sorted = useMemo(() => applySort(filtered, sort, sort.key !== 'playerName'), [filtered, sort]);

  // Build primary position lookup from fielding data
  const posMap = useMemo(() => {
    const POS = ['P','C','1B','2B','3B','SS','LF','CF','RF'];
    const m = new Map();
    for (const f of stats.fieldingSeason || []) {
      let best = null, max = 0;
      for (const k of POS) { const g = f['G_'+k]||0; if (g > max) { max = g; best = k; } }
      if (best) m.set(f.playerName, best);
    }
    return m;
  }, [stats.fieldingSeason]);

  function handleSort(key, numeric) {
    setSort(s => s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: numeric ? 'desc' : 'asc' });
  }
  return (
    <div>
      <div className="table-card">
        <div className="table-h">
          <h2>Season batting</h2>
          <span className="sub">{all.length} batters · {isMobile ? 'tap for details' : 'sortable · click a row for details'}</span>
          <span className="spacer"/>
          {!isMobile && <input className="search" placeholder="Search player…" value={q} onChange={e => setQ(e.target.value)}/>}
          {!isMobile && <button className="btn btn-secondary" onClick={() => exportCSV(BATTING_COLS, sorted, 'batting')}><I.Download size={14}/> CSV</button>}
        </div>
        {isMobile ? (
          <div style={{display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
            {sorted.map(p => <BattingCard key={p.playerId||p.playerName} p={p} onOpenPlayer={onOpenPlayer}/>)}
          </div>
        ) : (
        <div className="t-scroll">
          <table className="dt">
            <thead><tr>
              {BATTING_COLS.map(col => <SortHeader key={col.key} col={col} sort={sort} onSort={handleSort}/>)}
            </tr></thead>
            <tbody>
              {sorted.map(p => (
                <tr key={p.playerId||p.playerName} onClick={() => onOpenPlayer(p)}>
                  {BATTING_COLS.map(col => col.sticky ? (
                    <td key={col.key} className="first">
                      <div className="player-cell"><div className="player-num">{p.number}</div><span>{p.playerName.toUpperCase()}</span></div>
                    </td>
                  ) : (
                    <td key={col.key} className={`${col.rate?'rate':''} ${col.hi?'hi':''} ${(!col.rate&&!col.hi&&(p[col.key]===0||p[col.key]==null))?'zero':''}`}>
                      {fmt.cell(p[col.key], col.fmt)}
                    </td>
                  ))}
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={BATTING_COLS.length} style={{textAlign:'center',padding:36,color:'var(--text-3)'}}>No matching players.</td></tr>}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {/* ── Analytics section ───────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'center', gap:12,
        margin:'24px 0 12px', color:'var(--text-3)',
      }}>
        <div style={{flex:1, height:1, background:'var(--border)'}}/>
        <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',whiteSpace:'nowrap'}}>
          Batting analytics
        </span>
        <div style={{flex:1, height:1, background:'var(--border)'}}/>
      </div>

      {/* Contact vs Power matrix */}
      <ContactPowerMatrix players={all} onOpenPlayer={onOpenPlayer}/>

      {/* Hot / Cold leaderboard */}
      <HotColdLeaderboard players={all} onOpenPlayer={onOpenPlayer}/>

      {/* Lineup slot performance */}
      <LineupSlotPerformance players={all} posMap={posMap} onOpenPlayer={onOpenPlayer}/>

      {/* Plate discipline breakdown */}
      <PlateDiscipline players={all} posMap={posMap} onOpenPlayer={onOpenPlayer}/>
    </div>
  );
}

/* ─── Contact vs Power matrix ────────────────────────── */
function ContactPowerMatrix({ players, onOpenPlayer }) {
  const qualified = players.filter(p => p.AB >= 10);
  if (qualified.length < 2) return null;

  // Fixed axes — absolute scale, never relative to team
  // AVG: .100 – .600, midline at .350
  // SLG: .200 – 1.000, midline at .600
  const AVG_MIN = 0.100, AVG_MAX = 0.600, AVG_MID = 0.350;
  const SLG_MIN = 0.200, SLG_MAX = 1.000, SLG_MID = 0.600;

  // Clamp players that fall outside the range to the edge
  const clampAVG = v => Math.min(AVG_MAX, Math.max(AVG_MIN, v));
  const clampSLG = v => Math.min(SLG_MAX, Math.max(SLG_MIN, v));
  const W = 400, H = 260, ML = 32, MB = 24, MT = 8, MR = 8;
  const IW = W - ML - MR, IH = H - MB - MT;

  function xOf(avg) { return ML + ((avg - AVG_MIN) / (AVG_MAX - AVG_MIN)) * IW; }
  function yOf(slg) { return MT + (1 - (slg - SLG_MIN) / (SLG_MAX - SLG_MIN)) * IH; }

  const xMid = xOf(AVG_MID);
  const yMid = yOf(SLG_MID);

  const fmt2 = v => v >= 1 ? v.toFixed(3) : v.toFixed(3).replace(/^0\./, '.');
  const yTicks = [0.200, 0.400, 0.600, 0.800, 1.000];
  const xTicks = [0.100, 0.200, 0.350, 0.500, 0.600];

  return (
    <div className="card">
      <div className="card-h" style={{justifyContent:'center',textAlign:'center'}}>
        <h2>Contact vs Power</h2>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:'auto',display:'block'}}>
          {/* Quadrant fills */}
          <rect x={ML} y={MT} width={xMid-ML} height={yMid-MT} fill="rgba(230,57,70,0.05)"/>
          <rect x={xMid} y={MT} width={W-MR-xMid} height={yMid-MT} fill="rgba(62,196,145,0.07)"/>
          <rect x={ML} y={yMid} width={xMid-ML} height={H-MB-yMid} fill="rgba(100,100,120,0.04)"/>
          <rect x={xMid} y={yMid} width={W-MR-xMid} height={H-MB-yMid} fill="rgba(31,91,168,0.05)"/>
          {/* Border */}
          <rect x={ML} y={MT} width={IW} height={IH} fill="none" stroke="var(--border)" strokeWidth="1"/>
          {/* Reference lines */}
          <line x1={ML} y1={yMid} x2={W-MR} y2={yMid} stroke="var(--border-strong)" strokeWidth="1" strokeDasharray="4,3"/>
          <line x1={xMid} y1={MT} x2={xMid} y2={H-MB} stroke="var(--border-strong)" strokeWidth="1" strokeDasharray="4,3"/>
          {/* .400 AVG tick marker only — no full line */}
          <line x1={xOf(0.400)} y1={H-MB} x2={xOf(0.400)} y2={H-MB+4} stroke="var(--good)" strokeWidth="1.5" opacity="0.7"/>
          {/* Y axis ticks */}
          {yTicks.map((v,i) => (
            <text key={i} x={ML-3} y={yOf(v)+3} textAnchor="end" style={{fontSize:7,fill:'var(--text-3)',fontFamily:'var(--font-mono)'}}>{fmt2(v)}</text>
          ))}
          {/* X axis ticks */}
          {xTicks.map((v,i) => (
            <text key={i} x={xOf(v)} y={H-MB+10} textAnchor="middle" style={{fontSize:7,fill:'var(--text-3)',fontFamily:'var(--font-mono)'}}>{fmt2(v)}</text>
          ))}
          {/* Axis labels */}
          <text x={ML+IW/2} y={H} textAnchor="middle" style={{fontSize:8,fill:'var(--text-2)',fontWeight:700,fontFamily:'var(--font-display)'}}>AVG →</text>
          <text x={7} y={MT+IH/2} textAnchor="middle" transform={`rotate(-90,7,${MT+IH/2})`} style={{fontSize:8,fill:'var(--text-2)',fontWeight:700,fontFamily:'var(--font-display)'}}>SLG →</text>
          {/* Player dots */}
          {(() => {
            const R = 14; // minimum center-to-center distance
            // Compute initial positions
            const pts = qualified.map((p, i) => ({
              p,
              x: xOf(clampAVG(p.AVG)),
              y: yOf(clampSLG(p.SLG)),
            }));
            // Iterative collision separation — push overlapping dots apart
            for (let iter = 0; iter < 30; iter++) {
              for (let i = 0; i < pts.length; i++) {
                for (let j = i + 1; j < pts.length; j++) {
                  const dx = pts[j].x - pts[i].x;
                  const dy = pts[j].y - pts[i].y;
                  const dist = Math.sqrt(dx*dx + dy*dy) || 0.01;
                  if (dist < R) {
                    const push = (R - dist) / 2;
                    const nx = dx / dist, ny = dy / dist;
                    pts[i].x -= nx * push;
                    pts[i].y -= ny * push;
                    pts[j].x += nx * push;
                    pts[j].y += ny * push;
                  }
                }
              }
            }
            return pts.map(({ p, x, y }) => (
              <g key={p.playerName} style={{cursor:'pointer'}} onClick={() => onOpenPlayer(p)}>
                <circle cx={x} cy={y} r={14} fill="transparent"/>
                <circle cx={x} cy={y} r={6} fill="var(--text-2)" stroke="var(--border)" strokeWidth="1"/>
                <text x={x} y={y+0.5} textAnchor="middle" dominantBaseline="middle"
                  style={{fontSize:7,fontWeight:700,fill:'#000',pointerEvents:'none',fontFamily:'var(--font-mono)'}}>
                  {p.number}
                </text>
              </g>
            ));
          })()}
          {/* Quadrant corner labels — SVG text with rect background, drawn last */}
          {[
            {qx:ML,        qw:xMid-ML,    ty:MT+4,   label:'Low avg · Power', color:'var(--red)',    bg:'rgba(230,57,70,0.12)'},
            {qx:xMid,      qw:W-MR-xMid,  ty:MT+4,   label:'Elite zone',      color:'var(--good)',   bg:'rgba(62,196,145,0.12)'},
            {qx:ML,        qw:xMid-ML,    ty:H-MB-15,label:'Struggling',      color:'var(--text-2)', bg:'rgba(100,100,120,0.18)'},
            {qx:xMid,      qw:W-MR-xMid,  ty:H-MB-15,label:'Contact hitter',  color:'var(--text-2)', bg:'rgba(31,91,168,0.18)'},
          ].map(({qx,qw,ty,label,color,bg})=>{
            const h=12, pw=6;
            const charW=4.8;
            const w=Math.min(label.length*charW+pw*2, qw-8);
            const rx=qx+4, cx=rx+w/2;
            return (
              <g key={label}>
                <rect x={rx} y={ty} width={w} height={h} rx={2} fill={bg}/>
                <text x={cx} y={ty+h/2} textAnchor="middle" dominantBaseline="middle"
                  style={{fontSize:6.5,fontWeight:700,fill:color,fontFamily:'var(--font-display)',letterSpacing:'0.04em',pointerEvents:'none'}}>
                  {label}
                </text>
              </g>
            );
          })}
        </svg>

    </div>
  );
}

/* ─── Hot / Cold leaderboard ─────────────────────────── */
function HotColdLeaderboard({ players, onOpenPlayer }) {
  const withSpark = players.filter(p => p._spark?.length >= 3 && p.AB >= 8);
  if (withSpark.length < 2) return null;

  const scored = withSpark.map(p => {
    const last3 = p._spark.slice(-3);
    const prev  = p._spark.slice(-6, -3);
    const recent = last3.reduce((a,v)=>a+v,0)/last3.length;
    const baseline = prev.length ? prev.reduce((a,v)=>a+v,0)/prev.length : p.AVG;
    const delta = recent - baseline;
    return { ...p, recentAVG: recent, delta };
  }).sort((a,b) => b.delta - a.delta);

  const maxAbs = Math.max(...scored.map(p => Math.abs(p.delta)), 0.001);

  return (
    <div className="card">
      <div className="card-h">
        <h2>Hot &amp; Cold</h2>
        <span className="sub">Last 3 games vs prior 3 · based on hit rate trend</span>
      </div>
      <div style={{display:'flex',gap:16}}>
        {/* Hot */}
        <div style={{flex:1}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',color:'var(--good)',marginBottom:8}}>🔥 Heating up</div>
          {scored.filter(p=>p.delta>0).slice(0,5).map(p=>(
            <button key={p.playerName} onClick={()=>onOpenPlayer(p)} style={{
              width:'100%',display:'flex',alignItems:'center',gap:10,padding:'7px 0',
              borderBottom:'1px solid var(--border)',background:'none',textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',
            }}>
              <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.playerName.toUpperCase()}</div>
                <div style={{fontSize:10,color:'var(--text-3)'}}>Recent {fmt.avg(p.recentAVG)} · Season {fmt.avg(p.AVG)}</div>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--good)',fontFamily:'var(--font-mono)',flexShrink:0}}>
                +{(p.delta*1000).toFixed(0)}
              </div>
              <div style={{width:48,flexShrink:0}}><Sparkline data={p._spark.slice(-6)} w={48} h={20}/></div>
            </button>
          ))}
        </div>
        <div style={{width:1,background:'var(--border)'}}/>
        {/* Cold */}
        <div style={{flex:1}}>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',color:'var(--red)',marginBottom:8}}>🧊 Cooling down</div>
          {scored.filter(p=>p.delta<0).slice(-5).reverse().map(p=>(
            <button key={p.playerName} onClick={()=>onOpenPlayer(p)} style={{
              width:'100%',display:'flex',alignItems:'center',gap:10,padding:'7px 0',
              borderBottom:'1px solid var(--border)',background:'none',textAlign:'left',cursor:'pointer',font:'inherit',color:'var(--text)',
            }}>
              <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.playerName.toUpperCase()}</div>
                <div style={{fontSize:10,color:'var(--text-3)'}}>Recent {fmt.avg(p.recentAVG)} · Season {fmt.avg(p.AVG)}</div>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--red)',fontFamily:'var(--font-mono)',flexShrink:0}}>
                {(p.delta*1000).toFixed(0)}
              </div>
              <div style={{width:48,flexShrink:0}}><Sparkline data={p._spark.slice(-6)} w={48} h={20}/></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Projected Lineup ───────────────────────────────── */
function LineupSlotPerformance({ players, posMap, onOpenPlayer }) {
  const eligible = players.filter(p => p.PA >= 5);
  if (eligible.length < 4) return null;

  // Step 1: take top 10 by PA — these are our starters
  const top10 = [...eligible].sort((a,b) => (b.PA||0)-(a.PA||0)).slice(0,10);

  const FIELD_POS = ['P','C','1B','2B','3B','SS','LF','CF','RF'];

  // Step 2: assign each player their best available field position
  // Sort top10 by PA so highest-PA players get first pick of positions
  const assigned = [];
  const usedPos   = new Set();

  for (const p of top10) {
    // All positions this player has played, sorted by games desc
    const posByGames = FIELD_POS
      .map(k => ({ pos:k, g: (p['G_'+k]||0) }))
      .filter(x => x.g > 0)
      .sort((a,b) => b.g - a.g);
    const openPos = posByGames.find(x => !usedPos.has(x.pos));
    if (openPos) {
      assigned.push({ p, pos: openPos.pos, bats: true });
      usedPos.add(openPos.pos);
    } else {
      // No field position available — will be DP or Flex
      assigned.push({ p, pos: null, bats: true });
    }
  }

  // Step 3: In softball, exactly 1 DP (bats, no field position) and 1 Flex (fields, doesn't bat)
  // Players with no position assigned become DP candidates
  // The player with the lowest PA among fielders becomes the Flex candidate
  const noPos = assigned.filter(x => x.pos === null);
  const withPos = assigned.filter(x => x.pos !== null);

  let lineup, flex;

  if (noPos.length >= 1) {
    // Natural DP: best hitter (OPS) among those with no position
    const dp = noPos.sort((a,b) => (b.p.OPS||0)-(a.p.OPS||0))[0];
    dp.pos = 'DP';
    // Flex: lowest-PA fielder sits out of batting order
    const flexEntry = withPos.sort((a,b) => (a.p.PA||0)-(b.p.PA||0))[0];
    flexEntry.bats = false;
    flexEntry.pos = `${flexEntry.pos}/Flex`;
    lineup = assigned.filter(x => x.bats);
    flex = flexEntry;
  } else {
    // Everyone has a field position — lowest PA fielder becomes Flex, highest PA non-fielder is DP
    // Just take all 10 as batters with positions, flag the 10th as Flex
    const sorted = [...assigned].sort((a,b) => (b.p.PA||0)-(a.p.PA||0));
    // The 10th player (lowest PA) is the Flex
    const flexEntry = sorted[9];
    flexEntry.bats = false;
    flexEntry.pos = `${flexEntry.pos}/Flex`;
    lineup = sorted.filter(x => x.bats); // 9 batters
    flex = flexEntry;
  }

  // Step 4: order by Probable_Lineup_Slot from actual game data
  // Fall back to OPS heuristic only for players with no slot data
  const withSlot    = lineup.filter(x => x.p.Probable_Lineup_Slot >= 1);
  const withoutSlot = lineup.filter(x => !(x.p.Probable_Lineup_Slot >= 1));

  withSlot.sort((a,b) => a.p.Probable_Lineup_Slot - b.p.Probable_Lineup_Slot);
  withoutSlot.sort((a,b) => (b.p.OPS||0) - (a.p.OPS||0));

  const finalLineup = [...withSlot, ...withoutSlot];
  const maxOPS = Math.max(...finalLineup.map(x => x.p.OPS||0), flex?.p.OPS||0, 0.001);

  const rows = [...finalLineup, ...(flex ? [{ ...flex, slot:'Flex' }] : [])];

  return (
    <div className="card">
      <div className="card-h">
        <h2>Projected lineup</h2>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:0}}>
        {rows.map(({ p, pos, slot }, i) => {
          const isFlex = pos?.includes('Flex');
          const isDP   = pos === 'DP';
          return (
            <button key={p.playerName} onClick={()=>onOpenPlayer(p)} style={{
              display:'grid', gridTemplateColumns:'20px minmax(120px,200px) 1fr 100px',
              gap:12, alignItems:'center', padding:'8px 0',
              borderBottom:'1px solid var(--border)', background:'none',
              textAlign:'left', cursor:'pointer', font:'inherit',
              color: isFlex ? 'var(--text-3)' : 'var(--text)',
              opacity: isFlex ? 0.75 : 1,
            }}>
              <div style={{fontFamily:'var(--font-mono)',fontSize:11,fontWeight:700,color:'var(--text-3)',textAlign:'center'}}>
                {isFlex ? '—' : i+1}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
                <div style={{minWidth:0}}>
                  <span style={{fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'block'}}>
                    {p.playerName.toUpperCase()}{pos ? <span style={{fontWeight:500,color:'var(--text-3)',fontSize:11}}> · {pos}</span> : null}
                  </span>
                </div>
              </div>
              <div style={{height:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:3,overflow:'hidden'}}>
                <div style={{
                  height:'100%',
                  width:`${((p.OPS||0)/maxOPS)*100}%`,
                  background: isFlex
                    ? 'var(--border-strong)'
                    : `linear-gradient(90deg, var(--text-2), ${'var(--text-2)'})`,
                  transition:'width 600ms cubic-bezier(0.2,0.8,0.2,1)',
                }}/>
              </div>
              <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                {[['AVG',fmt.avg(p.AVG)],['OPS',fmt.avg(p.OPS)]].map(([k,v])=>(
                  <div key={k} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:1}}>
                    <span style={{fontSize:8,fontWeight:700,color:'var(--text-3)'}}>{k}</span>
                    <span className="mono" style={{fontSize:12,fontWeight:600}}>{v}</span>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Plate discipline breakdown ─────────────────────── */
function PlateDiscipline({ players, onOpenPlayer }) {
  const qualified = players.filter(p => p.PA >= 10).sort((a,b) => (b.OPS||0)-(a.OPS||0));
  if (qualified.length < 2) return null;

  return (
    <div className="card">
      <div className="card-h">
        <h2>Plate discipline</h2>
        <span className="sub">BB% · K% · BIP% per batter · min 10 PA</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:5}}>
        {/* Legend */}
        <div style={{display:'flex',gap:16,marginBottom:4}}>
          {[['var(--good)','BB%'],['var(--red)','K%'],['var(--text-2)','BIP%']].map(([c,l])=>(
            <div key={l} style={{display:'flex',alignItems:'center',gap:5,fontSize:10,color:'var(--text-3)'}}>
              <div style={{width:10,height:10,borderRadius:2,background:c,flexShrink:0}}/>
              {l}
            </div>
          ))}
        </div>
        {qualified.map(p => {
          const bb  = Math.min(p.BB_pct||0, 100);
          const k   = Math.min(p.SO_pct||0, 100);
          const bip = Math.max(0, Math.min(100 - bb - k, 100));
          return (
            <button key={p.playerName} onClick={()=>onOpenPlayer(p)} style={{
              display:'grid', gridTemplateColumns:'minmax(100px,180px) 1fr 80px',
              gap:12, alignItems:'center', padding:'6px 0',
              borderBottom:'1px solid var(--border)', background:'none',
              textAlign:'left', cursor:'pointer', font:'inherit', color:'var(--text)',
            }}>
              <div style={{display:'flex',alignItems:'center',gap:7,minWidth:0}}>
                <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
                <span style={{fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.playerName.toUpperCase()}</span>
              </div>
              <div style={{display:'flex',height:16,borderRadius:3,overflow:'hidden',gap:1}}>
                {bb>0  && <div title={`BB% ${bb.toFixed(1)}`}  style={{width:`${bb}%`, background:'var(--good)',  opacity:0.85}}/>}
                {k>0   && <div title={`K% ${k.toFixed(1)}`}    style={{width:`${k}%`,  background:'var(--red)',   opacity:0.85}}/>}
                {bip>0 && <div title={`BIP% ${bip.toFixed(1)}`}style={{width:`${bip}%`,background:'var(--text-2)',opacity:0.6}}/>}
              </div>
              <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                <span className="mono" style={{fontSize:10,color:'var(--good)',fontWeight:700}}>{bb.toFixed(1)}%</span>
                <span style={{color:'var(--border-strong)'}}>·</span>
                <span className="mono" style={{fontSize:10,color:'var(--red)',fontWeight:700}}>{k.toFixed(1)}%</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── PITCHING ───────────────────────────────────────────── */
const PITCHING_COLS = [
  { key: 'playerName', label: 'Pitcher', sticky: true, numeric: false },
  { key: 'Appearances', label: 'App' }, { key: 'GS', label: 'GS' },
  { key: 'IP', label: 'IP', fmt: 'ip' }, { key: 'BF', label: 'BF' },
  { key: 'H', label: 'H' }, { key: 'R', label: 'R' }, { key: 'ER', label: 'ER' },
  { key: 'BB', label: 'BB' }, { key: 'SO', label: 'K' },
  { key: 'HBP', label: 'HBP' }, { key: 'WP', label: 'WP' },
  { key: '#P', label: '#P' }, { key: 'TS', label: 'Strikes' },
  { key: 'StrikePct', label: 'Strike%', fmt: 'pct1' },
  { key: 'ERA', label: 'ERA', fmt: 'dec2', rate: true, hi: true },
  { key: 'WHIP', label: 'WHIP', fmt: 'dec2', rate: true },
  { key: 'K_per_7', label: 'K/7', fmt: 'dec2', rate: true },
];

export function PitchingTab({ stats, onOpenPlayer }) {
  const [sort, setSort] = useState({ key: 'ERA', dir: 'asc' });
  const all = stats.pitchingSeason.filter(p => p.Appearances > 0);
  const isMobile = useIsMobile();
  const sorted = useMemo(() => applySort(all, sort, sort.key !== 'playerName'), [all, sort]);
  function handleSort(key, numeric) {
    setSort(s => s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: numeric ? 'desc' : 'asc' });
  }
  if (!all.length) return <div className="empty"><h3>No pitching data yet</h3><p>Pitching stats will appear after games are played.</p></div>;
  const totalIP = all.reduce((s,p) => s+(p.IP||0), 0);
  const ranked = [...all].sort((a,b) => (b.IP||0)-(a.IP||0));
  const palette = ['#0B1F3A','#E63946','#1F5BA8','#5a7ba8','#9bb6e0','#c9a26b','#c4c8d0','#7a8aa3'];
  return (
    <div>
      <div className="table-card">
        <div className="table-h">
          <h2>Season pitching</h2><span className="sub">{all.length} pitchers</span>
          <span className="spacer"/>
          {!isMobile && <button className="btn btn-secondary" onClick={() => exportCSV(PITCHING_COLS, sorted, 'pitching')}><I.Download size={14}/> CSV</button>}
        </div>
        {isMobile ? (
          <div style={{display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
            {sorted.map(p => <PitchingCard key={p.playerName} p={p} onOpenPlayer={onOpenPlayer}/>)}
          </div>
        ) : (
        <div className="t-scroll">
          <table className="dt">
            <thead><tr>{PITCHING_COLS.map(c => <SortHeader key={c.key} col={c} sort={sort} onSort={handleSort}/>)}</tr></thead>
            <tbody>
              {sorted.map(p => (
                <tr key={p.playerName} onClick={() => onOpenPlayer({...p, playerId:'pit_'+p.playerName})}>
                  {PITCHING_COLS.map(col => col.sticky ? (
                    <td key={col.key} className="first"><div className="player-cell"><div className="player-num">{p.number}</div><span>{p.playerName.toUpperCase()}</span></div></td>
                  ) : (
                    <td key={col.key} className={`${col.rate?'rate':''} ${col.hi?'hi':''}`}>{fmt.cell(p[col.key],col.fmt)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {/* Workload */}
      <div className="card">
        <div className="card-h"><h2>Workload</h2><span className="sub">share of team innings pitched</span></div>
        <div style={{display:'flex',height:18,borderRadius:6,overflow:'hidden',border:'1px solid var(--border)',marginBottom:12}}>
          {ranked.map((p,i) => { const pct=totalIP>0?(p.IP/totalIP)*100:0; return <div key={p.playerName} title={`#${p.number} ${p.playerName} · ${fmt.ip(p.IP)} IP · ${pct.toFixed(1)}%`} style={{width:`${pct}%`,background:palette[i%palette.length]}}/>; })}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {ranked.map((p,i) => { const pct=totalIP>0?(p.IP/totalIP)*100:0; return (
            <div key={p.playerName} style={{display:'grid',gridTemplateColumns:'minmax(100px,160px) 1fr 50px 60px',gap:10,alignItems:'center'}}>
              <div style={{fontSize:12.5,fontWeight:600,display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                <span style={{width:10,height:10,borderRadius:2,background:palette[i%palette.length],flexShrink:0}}/>
                <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>#{p.number} {p.playerName.toUpperCase()}</span>
              </div>
              <div style={{height:8,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:2,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pct}%`,background:palette[i%palette.length],transition:'width 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'}}/>
              </div>
              <div className="mono" style={{fontSize:12,color:'var(--text-2)',textAlign:'right'}}>{fmt.ip(p.IP)} IP</div>
              <div className="mono" style={{fontSize:12.5,fontWeight:700,textAlign:'right'}}>{pct.toFixed(1)}%</div>
            </div>
          ); })}
        </div>
        <div style={{fontSize:11,color:'var(--text-3)',marginTop:10}}>Team total: {fmt.ip(totalIP)} IP across {ranked.length} pitchers.</div>
      </div>
      {/* Strike rate */}
      <div className="card">
        <div className="card-h"><h2>Strike rate breakdown</h2><span className="sub">% of pitches in the strike zone</span></div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {sorted.map(p => (
            <div key={p.playerName} style={{display:'grid',gridTemplateColumns:'minmax(100px,160px) 1fr 60px',gap:12,alignItems:'center'}}>
              <div style={{fontSize:13,fontWeight:600}}>#{p.number} {p.playerName.toUpperCase()}</div>
              <div style={{height:18,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:9,overflow:'hidden',position:'relative'}}>
                <div style={{height:'100%',width:`${p.StrikePct}%`,background:'linear-gradient(90deg, var(--text-2), var(--red))',transition:'width 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'}}/>
                <div style={{position:'absolute',left:'60%',top:0,height:'100%',borderLeft:'1px dashed rgba(0,0,0,0.15)'}}/>
              </div>
              <div className="mono" style={{fontSize:13,fontWeight:600}}>{(p.StrikePct||0).toFixed(1)}%</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:11,color:'var(--text-3)',marginTop:10}}>Dashed line marks the league benchmark of 60%.</div>
      </div>
      {/* K%/BB% */}
      <div className="card">
        <div className="card-h"><h2>Strikeout &amp; walk rates</h2><span className="sub">share of batters faced</span></div>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr 1fr',columnGap:16,rowGap:8,alignItems:'center'}}>
          <div/>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',color:'var(--text-3)'}}>K%</div>
          <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',color:'var(--text-3)'}}>BB%</div>
          {sorted.map(p => {
            const bf=p.BF||1, kPct=(p.SO/bf)*100, bbPct=(p.BB/bf)*100, max=40;
            return (
              <React.Fragment key={p.playerName}>
                <div style={{fontSize:12.5,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',minWidth:0}}>#{p.number} {p.playerName.toUpperCase()}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 48px',gap:8,alignItems:'center'}}>
                  <div style={{height:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.min(100,(kPct/max)*100)}%`,background:'var(--red)',transition:'width 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'}}/>
                  </div>
                  <div className="mono" style={{fontSize:12,fontWeight:700,textAlign:'right'}}>{kPct.toFixed(1)}%</div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 48px',gap:8,alignItems:'center'}}>
                  <div style={{height:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.min(100,(bbPct/max)*100)}%`,background:'var(--text-2)',opacity:0.6,transition:'width 600ms cubic-bezier(0.2, 0.8, 0.2, 1)'}}/>
                  </div>
                  <div className="mono" style={{fontSize:12,fontWeight:700,textAlign:'right'}}>{bbPct.toFixed(1)}%</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div style={{fontSize:11,color:'var(--text-3)',marginTop:10}}>Bars scale to a 40% reference; higher K% and lower BB% are better.</div>
      </div>

      {/* ── Pitching analytics section ───────────────────── */}
      <div style={{display:'flex',alignItems:'center',gap:12,margin:'24px 0 12px',color:'var(--text-3)'}}>
        <div style={{flex:1,height:1,background:'var(--border)'}}/>
        <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',whiteSpace:'nowrap'}}>Pitching analytics</span>
        <div style={{flex:1,height:1,background:'var(--border)'}}/>
      </div>

      <PitchEfficiency pitchers={all} onOpenPlayer={onOpenPlayer}/>
      <KBBRatio pitchers={all} onOpenPlayer={onOpenPlayer}/>
      <ERATimeline pitchers={all} gameLog={stats.teamGameLog} onOpenPlayer={onOpenPlayer}/>
    </div>
  );
}

/* ─── Pitch count efficiency ─────────────────────────── */
function PitchEfficiency({ pitchers, onOpenPlayer }) {
  const qualified = pitchers.filter(p => (p['#P']||0) >= 20).sort((a,b) => (b.IP||0)-(a.IP||0));
  if (!qualified.length) return null;

  const ipToOuts = ip => { const i=Math.trunc(ip||0), f=Math.round(((ip||0)-i)*10); return i*3+f; };

  return (
    <div className="card">
      <div className="card-h">
        <h2>Pitch count efficiency</h2>
        <span className="sub">Pitches per out · strikes per pitch · lower P/Out is better</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:0}}>
        {qualified.map(p => {
          const outs    = ipToOuts(p.IP||0);
          const pPerOut = outs > 0 ? (p['#P']||0) / outs : 0;
          const sPct    = p['#P'] > 0 ? ((p.TS||0) / p['#P']) * 100 : 0;
          // P/Out scale: 3.0 is excellent, 6.0 is poor (reference 4.5)
          const pOutPct  = Math.min(100, (pPerOut / 6) * 100);
          const pOutColor = pPerOut <= 3.5 ? 'var(--good)' : pPerOut <= 5.0 ? 'var(--text-2)' : 'var(--red)';
          return (
            <button key={p.playerName} onClick={()=>onOpenPlayer({...p,playerId:'pit_'+p.playerName})} style={{
              display:'grid', gridTemplateColumns:'minmax(100px,180px) 1fr 55px 1fr 55px',
              gap:10, alignItems:'center', padding:'8px 0',
              borderBottom:'1px solid var(--border)', background:'none',
              textAlign:'left', cursor:'pointer', font:'inherit', color:'var(--text)',
            }}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
                <span style={{fontSize:12,fontWeight:700}}>{p.playerName.toUpperCase()}</span>
              </div>
              {/* P/Out bar */}
              <div style={{height:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${pOutPct}%`,background:pOutColor,transition:'width 600ms ease'}}/>
              </div>
              <div className="mono" style={{fontSize:12,fontWeight:700,color:pOutColor,textAlign:'right'}}>{pPerOut.toFixed(1)}</div>
              {/* Strike% bar */}
              <div style={{height:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:3,overflow:'hidden',position:'relative'}}>
                <div style={{height:'100%',width:`${sPct}%`,background:'var(--good)',opacity:0.7,transition:'width 600ms ease'}}/>
                <div style={{position:'absolute',left:'60%',top:0,height:'100%',borderLeft:'1px dashed var(--border-strong)'}}/>
              </div>
              <div className="mono" style={{fontSize:12,fontWeight:700,textAlign:'right'}}>{sPct.toFixed(0)}%</div>
            </button>
          );
        })}
      </div>
      <div style={{display:'flex',gap:20,marginTop:10,fontSize:11,color:'var(--text-3)'}}>
        <span>Left bars: pitches per out (lower = more efficient)</span>
        <span>Right bars: strike% (dashed = 60% benchmark)</span>
      </div>
    </div>
  );
}

/* ─── K/BB ratio visual ──────────────────────────────── */
function KBBRatio({ pitchers, onOpenPlayer }) {
  const qualified = pitchers.filter(p => (p.BB||0) > 0 && (p.BF||0) >= 10).sort((a,b) => (b.IP||0)-(a.IP||0));
  if (!qualified.length) return null;

  const maxRatio = Math.max(...qualified.map(p => (p.SO||0)/(p.BB||1)), 1);

  return (
    <div className="card">
      <div className="card-h">
        <h2>K/BB ratio</h2>
        <span className="sub">Strikeouts per walk · higher is better · 2.0+ is solid · 3.0+ is excellent</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {qualified.map(p => {
          const ratio = (p.SO||0) / (p.BB||1);
          const pct   = Math.min(100, (ratio / Math.max(maxRatio, 4)) * 100);
          const color = ratio >= 3.0 ? 'var(--good)' : ratio >= 2.0 ? 'var(--text-2)' : 'var(--red)';
          const ref2  = Math.min(100, (2.0 / Math.max(maxRatio, 4)) * 100);
          const ref3  = Math.min(100, (3.0 / Math.max(maxRatio, 4)) * 100);
          return (
            <button key={p.playerName} onClick={()=>onOpenPlayer({...p,playerId:'pit_'+p.playerName})} style={{
              display:'grid', gridTemplateColumns:'minmax(100px,180px) 1fr 70px',
              gap:12, alignItems:'center', padding:'6px 0',
              borderBottom:'1px solid var(--border)', background:'none',
              textAlign:'left', cursor:'pointer', font:'inherit', color:'var(--text)',
            }}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
                <div style={{display:'flex',flexDirection:'column',gap:1}}>
                  <span style={{fontSize:12,fontWeight:700}}>{p.playerName.toUpperCase()}</span>
                  <span style={{fontSize:10,color:'var(--text-3)'}}>{p.SO} K · {p.BB} BB</span>
                </div>
              </div>
              <div style={{height:14,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:4,overflow:'hidden',position:'relative'}}>
                <div style={{height:'100%',width:`${pct}%`,background:color,opacity:0.8,transition:'width 600ms ease',borderRadius:4}}/>
                {/* Reference lines at 2.0 and 3.0 */}
                <div style={{position:'absolute',left:`${ref2}%`,top:0,height:'100%',borderLeft:'1px dashed var(--border-strong)'}}/>
                <div style={{position:'absolute',left:`${ref3}%`,top:0,height:'100%',borderLeft:'1px dashed var(--good)',opacity:0.5}}/>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>
                <span className="mono" style={{fontSize:15,fontWeight:700,color}}>{ratio.toFixed(1)}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div style={{fontSize:11,color:'var(--text-3)',marginTop:8}}>
        Gray dashed = 2.0 (solid) · Green dashed = 3.0 (excellent)
      </div>
    </div>
  );
}

/* ─── ERA timeline ───────────────────────────────────── */
function ERATimeline({ pitchers, gameLog, onOpenPlayer }) {
  const qualified = pitchers.filter(p => p.Appearances >= 3).sort((a,b) => (b.IP||0)-(a.IP||0)).slice(0,4);
  if (!qualified.length || !gameLog?.length) return null;

  // Build rolling ERA across season by computing cumulative ER/IP after each game
  // We don't have per-game pitcher splits, so we prorate season totals across games
  // weighted by pit_ER from the game log
  const completedGames = gameLog.filter(g => (g.bat_AB||0)>0 || (g.bat_R||0)>0 || (g.pit_IP||0)>0);
  if (completedGames.length < 3) return null;

  const ipToOuts = ip => { const i=Math.trunc(ip||0), f=Math.round(((ip||0)-i)*10); return i*3+f; };
  const outsToIP = o => Math.trunc(o/3) + (o%3)/10;

  // For each pitcher, compute rolling ERA after each game
  // Prorate their season ER proportionally to each game's team ER
  const teamTotalER = completedGames.reduce((s,g)=>s+(g.pit_ER||0),0)||1;

  const series = qualified.map(p => {
    let cumER=0, cumOuts=0;
    const points = completedGames.map((g, i) => {
      const gameER   = ((p.ER||0)/teamTotalER) * (g.pit_ER||0);
      const gameOuts = ipToOuts(p.IP||0) / completedGames.length;
      cumER   += gameER;
      cumOuts += gameOuts;
      const era = cumOuts > 0 ? (cumER * 21) / cumOuts : 0; // *21 = 7 innings
      return era;
    });
    return { p, points };
  });

  // Chart dimensions
  const W=500, H=160, ML=36, MB=24, MT=12, MR=12;
  const IW=W-ML-MR, IH=H-MB-MT;
  const n = completedGames.length;
  const allPoints = series.flatMap(s=>s.points);
  const minERA=0, maxERA=Math.max(8, ...allPoints)+0.5;

  function xOf(i)  { return ML + (i/(n-1))*IW; }
  function yOf(v)  { return MT + (1-((v-minERA)/(maxERA-minERA)))*IH; }

  const COLORS = ['var(--good)','var(--red)','var(--text-2)','var(--warn)'];

  return (
    <div className="card">
      <div className="card-h">
        <h2>ERA trend</h2>
        <span className="sub">Rolling ERA across the season · prorated from team game log</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:'auto',display:'block'}}>
        {/* Grid lines */}
        {[0,2,4,6,8].filter(v=>v<=maxERA).map(v=>(
          <g key={v}>
            <line x1={ML} y1={yOf(v)} x2={W-MR} y2={yOf(v)} stroke="var(--border)" strokeWidth="1"/>
            <text x={ML-3} y={yOf(v)+3} textAnchor="end" style={{fontSize:7,fill:'var(--text-3)',fontFamily:'var(--font-mono)'}}>{v}.00</text>
          </g>
        ))}
        {/* Border */}
        <rect x={ML} y={MT} width={IW} height={IH} fill="none" stroke="var(--border)" strokeWidth="1"/>
        {/* ERA lines */}
        {series.map(({p,points},si) => {
          const color = COLORS[si%COLORS.length];
          const d = points.map((v,i)=>`${i===0?'M':'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');
          const last = points[points.length-1];
          return (
            <g key={p.playerName}>
              <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" opacity="0.85"/>
              <circle cx={xOf(points.length-1)} cy={yOf(last)} r={3} fill={color}/>
            </g>
          );
        })}
        {/* X axis label */}
        <text x={ML+IW/2} y={H} textAnchor="middle" style={{fontSize:8,fill:'var(--text-3)',fontFamily:'var(--font-display)'}}>Game →</text>
      </svg>
      {/* Legend */}
      <div style={{display:'flex',gap:16,flexWrap:'wrap',marginTop:8}}>
        {series.map(({p},si)=>(
          <div key={p.playerName} onClick={()=>onOpenPlayer({...p,playerId:'pit_'+p.playerName})}
            style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:11}}>
            <div style={{width:18,height:3,borderRadius:2,background:COLORS[si%COLORS.length],flexShrink:0}}/>
            <span style={{fontWeight:700}}>#{p.number} {p.playerName.toUpperCase()}</span>
            <span style={{color:'var(--text-3)'}}>· {p.ERA?.toFixed(2)} ERA</span>
          </div>
        ))}
      </div>
      <div style={{fontSize:11,color:'var(--text-3)',marginTop:6}}>
        Note: individual game splits are not tracked — ERA trend is estimated from season totals proportionally.
      </div>
    </div>
  );
}

/* ─── FIELDING ───────────────────────────────────────────── */
const FPOSITIONS = [['G_P','P'],['G_C','C'],['G_1B','1B'],['G_2B','2B'],['G_3B','3B'],['G_SS','SS'],['G_LF','LF'],['G_CF','CF'],['G_RF','RF'],['G_DH','DH']];

export function FieldingTab({ stats }) {
  const isMobile = useIsMobile();
  const fielders = stats.fieldingSeason.filter(p => p.Games > 0).sort((a,b) => a.playerName.localeCompare(b.playerName));
  if (!fielders.length) return <div className="empty"><h3>No fielding data</h3></div>;
  const totals = FPOSITIONS.map(([k,lab]) => ({
    pos:lab, total:fielders.reduce((s,p)=>s+(p[k]||0),0),
    contributors:fielders.filter(p=>p[k]>0).map(p=>({name:p.playerName,n:p.number,g:p[k]})).sort((a,b)=>b.g-a.g),
  })).filter(x=>x.total>0).sort((a,b)=>b.total-a.total);
  const posMax = Math.max(...totals.map(t=>t.total));

  // Get primary position for a player
  function primaryPos(p) {
    let best=null,max=0;
    for (const [k,lab] of FPOSITIONS) { const g=p[k]||0; if(g>max){max=g;best=lab;} }
    return best;
  }

  return (
    <div>
      <div className="table-card">
        <div className="table-h"><h2>Games at position</h2><span className="sub">{fielders.length} fielders</span></div>
        {isMobile ? (
          <div style={{display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
            {fielders.map(p => {
              const pos = primaryPos(p);
              const positions = FPOSITIONS.filter(([k])=>p[k]>0);
              return (
                <div key={p.playerName} style={{
                  padding:'10px 12px',background:'var(--surface)',
                  border:'1px solid var(--border)',borderRadius:10,
                }}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                    <div className="player-num" style={{flexShrink:0}}>{p.number}</div>
                    <div style={{fontWeight:700,fontSize:13,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {p.playerName.toUpperCase()}{pos?<span style={{fontWeight:500,color:'var(--text-3)',fontSize:11}}> · {pos}</span>:null}
                    </div>
                    {p.E>0&&<div style={{fontSize:11,fontWeight:700,color:'var(--red)'}}>E: {p.E}</div>}
                  </div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {positions.map(([k,lab])=>(
                      <div key={k} style={{
                        padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:700,
                        background: lab===pos?'var(--navy-900)':'var(--surface-2)',
                        color: lab===pos?'#fff':'var(--text-2)',
                        border:'1px solid var(--border)',
                      }}>{lab}: {p[k]}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="t-scroll">
          <table className="dt">
            <thead><tr><th className="first">Player</th><th>G</th>{FPOSITIONS.map(([k,lab])=><th key={k}>{lab}</th>)}<th>E</th></tr></thead>
            <tbody>
              {fielders.map(p=>(
                <tr key={p.playerName}>
                  <td className="first"><div className="player-cell"><div className="player-num">{p.number}</div><span>{p.playerName.toUpperCase()}</span></div></td>
                  <td>{p.Games}</td>
                  {FPOSITIONS.map(([k])=><td key={k} className={p[k]>0?'rate':'zero'}>{p[k]||'—'}</td>)}
                  <td className={p.E>0?'err':'zero'}>{p.E||0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      <div className="split">
        <div className="card">
          <div className="card-h"><h2>Errors heatmap</h2><span className="sub">Bigger = more errors</span></div>
          <div className="diamond-wrap"><ErrorsDiamond errors={stats.defensiveErrors}/></div>
        </div>
        <div className="card">
          <div className="card-h"><h2>Position usage</h2><span className="sub">Total games per position</span></div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {totals.map(t=>(
              <div key={t.pos}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                  <span style={{fontSize:12,fontWeight:700}}>{t.pos}</span>
                  <span style={{fontSize:11,color:'var(--text-3)'}}>{t.contributors.slice(0,2).map(c=>`#${c.n} (${c.g})`).join(' · ')}{t.contributors.length>2&&` +${t.contributors.length-2}`}</span>
                </div>
                <div style={{display:'flex',height:14,gap:1,background:'var(--bg)',borderRadius:4,overflow:'hidden'}}>
                  {t.contributors.map((c,i)=><div key={i} title={`#${c.n} ${c.name}: ${c.g} games`} style={{width:`${(c.g/posMax)*100}%`,background:i===0?'var(--text-2)':i===1?'var(--text-3)':'var(--border-strong)',minWidth:2}}/>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── GAME LOG ───────────────────────────────────────────── */
export function GameLogTab({ stats, onOpenGame }) {
  const isMobile = useIsMobile();
  const log = stats.teamGameLog.filter(g =>
    (g.bat_AB||0) > 0 || (g.bat_R||0) > 0 || (g.pit_IP||0) > 0
  );
  if (!log?.length) return <div className="empty"><h3>No games yet</h3></div>;

  function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt)) return String(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return (
    <div>
      <div className="table-card">
        <div className="table-h"><h2>Game log</h2><span className="sub">{log.length} games · click for box score</span></div>
        {isMobile ? (
          <div style={{display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
            {log.map(g => <GameCard key={g.gameId} g={g} onOpenGame={onOpenGame}/>)}
          </div>
        ) : (
        <div className="t-scroll">
          <table className="dt">
            <thead>
              <tr>
                <th className="first" style={{textAlign:'center'}}></th>
                <th colSpan="4" style={{textAlign:'center'}}></th>
                <th colSpan="5" style={{textAlign:'center',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)'}}>BATTING</th>
                <th colSpan="5" style={{textAlign:'center'}}>PITCHING</th>
              </tr>
              <tr>
                <th className="first" style={{textAlign:'center',borderTop:'1px solid var(--border)'}}>Game</th>
                <th style={{textAlign:'center'}}>Date</th>
                <th style={{textAlign:'center'}}>Result</th>
                <th style={{textAlign:'center'}}>RS</th>
                <th style={{textAlign:'center'}}>RA</th>
                <th style={{textAlign:'center'}}>AB</th><th style={{textAlign:'center'}}>H</th><th style={{textAlign:'center'}}>RBI</th><th style={{textAlign:'center'}}>BB</th><th style={{textAlign:'center'}}>K</th>
                <th style={{textAlign:'center'}}>IP</th><th style={{textAlign:'center'}}>H</th><th style={{textAlign:'center'}}>ER</th><th style={{textAlign:'center'}}>BB</th><th style={{textAlign:'center'}}>K</th>
              </tr>
            </thead>
            <tbody>
              {log.map(g=>(
                <tr key={g.gameId} onClick={()=>onOpenGame(g)}>
                  <td className="first"><div style={{fontWeight:600,fontFamily:'var(--font-sans)'}}>{g.linkText}</div></td>
                  <td className="zero">{fmtDate(g.date)}</td>
                  <td><span className={`result-pill ${g.Result}`}>{g.Result}</span></td>
                  <td style={g.bat_R>g.pit_R?{color:'#fff',fontWeight:700}:{}}>{g.bat_R}</td>
                  <td style={g.pit_R>g.bat_R?{color:'#fff',fontWeight:700}:{color:'var(--text-3)'}}>{g.pit_R}</td>
                  <td>{g.bat_AB}</td><td>{g.bat_H}</td><td>{g.bat_RBI}</td><td>{g.bat_BB}</td><td>{g.bat_SO}</td>
                  <td>{fmt.ip(g.pit_IP)}</td><td>{g.pit_H}</td><td>{g.pit_ER}</td><td>{g.pit_BB}</td><td>{g.pit_SO}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
